/**
 * Valuation Method Comparison Component
 * 
 * Visual comparison of Average Cost vs FIFO valuation methods
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";

interface ComparisonScenario {
  description: string;
  purchases: Array<{
    date: string;
    quantity: number;
    unitCost: number;
  }>;
  sale: {
    quantity: number;
  };
}

const scenario: ComparisonScenario = {
  description: "Rising cost scenario - typical during inflation",
  purchases: [
    { date: "Jan 1", quantity: 100, unitCost: 10.00 },
    { date: "Jan 15", quantity: 75, unitCost: 11.50 },
    { date: "Feb 1", quantity: 50, unitCost: 12.00 },
  ],
  sale: {
    quantity: 120,
  },
};

export function ValuationMethodComparison() {
  // Calculate Average Cost
  const totalQty = scenario.purchases.reduce((sum, p) => sum + p.quantity, 0);
  const totalValue = scenario.purchases.reduce((sum, p) => sum + p.quantity * p.unitCost, 0);
  const avgCost = totalValue / totalQty;
  const avgCOGS = scenario.sale.quantity * avgCost;

  // Calculate FIFO
  let remainingToSell = scenario.sale.quantity;
  let fifoCOGS = 0;
  const fifoBreakdown: Array<{ from: string; qty: number; cost: number; total: number }> = [];

  for (const purchase of scenario.purchases) {
    if (remainingToSell <= 0) break;
    
    const qtyFromThisPurchase = Math.min(remainingToSell, purchase.quantity);
    const costFromThisPurchase = qtyFromThisPurchase * purchase.unitCost;
    
    fifoBreakdown.push({
      from: purchase.date,
      qty: qtyFromThisPurchase,
      cost: purchase.unitCost,
      total: costFromThisPurchase,
    });
    
    fifoCOGS += costFromThisPurchase;
    remainingToSell -= qtyFromThisPurchase;
  }

  const fifoAvgCost = fifoCOGS / scenario.sale.quantity;
  const difference = avgCOGS - fifoCOGS;
  const differencePercent = (difference / avgCOGS) * 100;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Valuation Method Comparison</CardTitle>
          <CardDescription>
            How Average Cost and FIFO differ in a rising cost scenario
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Scenario */}
          <div>
            <h4 className="font-semibold mb-3">Scenario: {scenario.description}</h4>
            <div className="space-y-2">
              <div className="text-sm font-medium">Purchases:</div>
              {scenario.purchases.map((purchase, index) => (
                <div key={index} className="text-sm text-muted-foreground ml-4">
                  {purchase.date}: {purchase.quantity} units @ £{purchase.unitCost.toFixed(2)} each
                </div>
              ))}
              <div className="text-sm font-medium mt-3">Sale:</div>
              <div className="text-sm text-muted-foreground ml-4">
                Sell {scenario.sale.quantity} units
              </div>
            </div>
          </div>

          {/* Comparison Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Average Cost Method */}
            <Card className="border-2 border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  Average Cost Method
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Current
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Calculation:</div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>Total Value: £{totalValue.toFixed(2)}</div>
                    <div>Total Quantity: {totalQty} units</div>
                    <div className="font-semibold text-foreground">
                      Avg Cost: £{avgCost.toFixed(2)}/unit
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-sm font-medium mb-2">COGS Calculation:</div>
                  <div className="text-sm text-muted-foreground">
                    {scenario.sale.quantity} units × £{avgCost.toFixed(2)}
                  </div>
                  <div className="text-xl font-bold mt-2">
                    = £{avgCOGS.toFixed(2)}
                  </div>
                </div>

                <div className="text-xs text-muted-foreground">
                  ✓ Simple calculation
                  <br />
                  ✓ Smooths out price fluctuations
                  <br />
                  ✓ Easy to implement
                </div>
              </CardContent>
            </Card>

            {/* FIFO Method */}
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  FIFO Method
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    New
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Calculation:</div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {fifoBreakdown.map((item, index) => (
                      <div key={index}>
                        {item.qty} units from {item.from} @ £{item.cost.toFixed(2)} = £
                        {item.total.toFixed(2)}
                      </div>
                    ))}
                    <div className="font-semibold text-foreground mt-2">
                      Avg Cost: £{fifoAvgCost.toFixed(2)}/unit
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-sm font-medium mb-2">COGS Calculation:</div>
                  <div className="text-sm text-muted-foreground">
                    Sum of oldest layers first
                  </div>
                  <div className="text-xl font-bold mt-2">
                    = £{fifoCOGS.toFixed(2)}
                  </div>
                </div>

                <div className="text-xs text-muted-foreground">
                  ✓ Matches physical flow
                  <br />
                  ✓ Better for perishables
                  <br />
                  ✓ More accurate valuation
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Difference Analysis */}
          <Card className="border-2 border-purple-200 bg-purple-50/50">
            <CardHeader>
              <CardTitle className="text-lg">Impact Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">COGS Difference</div>
                  <div className="text-2xl font-bold flex items-center justify-center gap-2">
                    {difference > 0 ? (
                      <>
                        <TrendingDown className="h-5 w-5 text-green-600" />
                        <span className="text-green-600">-£{Math.abs(difference).toFixed(2)}</span>
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-5 w-5 text-red-600" />
                        <span className="text-red-600">+£{Math.abs(difference).toFixed(2)}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    FIFO vs Average Cost
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Percentage Impact</div>
                  <div className="text-2xl font-bold">
                    {Math.abs(differencePercent).toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {difference > 0 ? "Lower COGS" : "Higher COGS"}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Profit Impact</div>
                  <div className="text-2xl font-bold flex items-center justify-center gap-2">
                    {difference > 0 ? (
                      <>
                        <TrendingUp className="h-5 w-5 text-green-600" />
                        <span className="text-green-600">+£{Math.abs(difference).toFixed(2)}</span>
                      </>
                    ) : (
                      <>
                        <TrendingDown className="h-5 w-5 text-red-600" />
                        <span className="text-red-600">-£{Math.abs(difference).toFixed(2)}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Higher profit with FIFO
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white rounded-lg border">
                <div className="text-sm font-medium mb-2">Key Insight:</div>
                <div className="text-sm text-muted-foreground">
                  In a <strong>rising cost environment</strong>, FIFO results in{" "}
                  <strong className="text-green-600">lower COGS</strong> because it uses older,
                  cheaper costs first. This leads to{" "}
                  <strong className="text-green-600">higher reported profits</strong> and{" "}
                  <strong>more accurate inventory valuation</strong> at current costs.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* When to Use Each Method */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Use Average Cost When:</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>✓ Costs are relatively stable</div>
                <div>✓ Items are interchangeable/commodity</div>
                <div>✓ Simplicity is preferred</div>
                <div>✓ Items don't have expiry dates</div>
                <div>✓ High volume, low-value items</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Use FIFO When:</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>✓ Costs are rising (inflation)</div>
                <div>✓ Items are perishable</div>
                <div>✓ Fashion/seasonal items</div>
                <div>✓ Electronics (obsolescence risk)</div>
                <div>✓ Accurate valuation is critical</div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
